import React, { useMemo } from "react";

import {
	Badge,
	InfoMessage,
	Spinner,
	Table,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { differenceInSeconds, format } from "date-fns";
import { t } from "i18next";

import { useQuery } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";

import { adminKeys } from "@/constants/CQueryKeys";

import { TAdminJobRun } from "@/types/TAdmin";

const STATUS_COLOR = {
	running: "info",
	success: "success",
	failed: "danger",
} as const;

const when = (iso: string | null) =>
	iso ? format(new Date(iso), "dd/MM/yyyy HH:mm") : t("admin.jobs.never");

const durationOf = (run: TAdminJobRun) =>
	run.finishedAt
		? `${differenceInSeconds(new Date(run.finishedAt), new Date(run.startedAt))} s`
		: "—";

const StatusBadge: React.FC<{ run: TAdminJobRun }> = ({ run }) => (
	<Badge color={STATUS_COLOR[run.status]} variant="tint" size="sm">
		{t(`admin.jobs.status.${run.status}`)}
	</Badge>
);

/**
 * The scheduled jobs tab: one card per job (last run, last success, late
 * when no success in 26 h), then the latest runs with their errors - what
 * `docker compose logs scheduler` used to be the only way to know.
 */
export const AdminJobs: React.FC = () => {
	const { data, isLoading, error } = useQuery({
		queryKey: adminKeys.jobs,
		queryFn: AdminService.jobs,
	});

	const columns: TTableColumn<TAdminJobRun>[] = useMemo(
		() => [
			{
				key: "job",
				label: t("admin.jobs.columns.job"),
				width: "1fr",
				render: (run) => (
					<span className="font-mono text-sm">{run.job}</span>
				),
			},
			{
				key: "startedAt",
				label: t("admin.jobs.columns.started"),
				render: (run) => when(run.startedAt),
			},
			{
				key: "finishedAt",
				label: t("admin.jobs.columns.duration"),
				render: durationOf,
			},
			{
				key: "status",
				label: t("admin.jobs.columns.status"),
				width: "1fr",
				truncate: false,
				render: (run) => (
					<span className="flex flex-col gap-1">
						<StatusBadge run={run} />
						{run.error && (
							<span
								className="text-xs text-neutral-500"
								data-testid="job-error"
							>
								{run.error}
							</span>
						)}
					</span>
				),
			},
		],
		[]
	);

	if (isLoading) return <Spinner />;
	if (error || !data)
		return <InfoMessage color="danger">{t("admin.error")}</InfoMessage>;

	return (
		<div className="flex flex-col gap-6">
			<p className="text-sm text-neutral-500">{t("admin.jobs.intro")}</p>
			<ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
				{data.jobs.map((job) => (
					<li
						key={job.job}
						className="flex flex-col gap-2 rounded-md border border-neutral-200 p-4"
						data-testid={`job-${job.job}`}
					>
						<span className="flex items-center justify-between gap-2">
							<span className="font-mono text-sm font-medium">
								{job.job}
							</span>
							<Badge
								color={job.isStale ? "danger" : "success"}
								variant="tint"
								size="sm"
							>
								{job.isStale
									? t("admin.jobs.stale")
									: t("admin.jobs.ok")}
							</Badge>
						</span>
						<span className="flex items-center justify-between text-sm">
							<span className="text-neutral-500">
								{t("admin.jobs.lastRun")}
							</span>
							<span className="flex items-center gap-2">
								{when(job.lastRun?.startedAt ?? null)}
								{job.lastRun && (
									<StatusBadge run={job.lastRun} />
								)}
							</span>
						</span>
						<span className="flex items-center justify-between text-sm">
							<span className="text-neutral-500">
								{t("admin.jobs.lastSuccess")}
							</span>
							<span>{when(job.lastSuccessAt)}</span>
						</span>
						{job.lastRun?.error && (
							<span className="text-danger-600 text-xs">
								{job.lastRun.error}
							</span>
						)}
					</li>
				))}
			</ul>
			<div className="flex flex-col gap-2">
				<h2 className="text-sm font-medium">
					{t("admin.jobs.recent")}
				</h2>
				<Table
					items={data.recent}
					getItemKey={(run) => run.id}
					columns={columns}
				/>
			</div>
		</div>
	);
};
